import { LoadingBlockFullPage } from '@/components/loading/loading-block-full-page';
import { useAppState } from '@/lib/hooks/use-app-state';
import type { AppConfig } from '@/types/app-config-type';
import { api } from '@/utils/api';
import { AuthClient } from '@/utils/auth-client';
import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';

interface AclContextValue {
    authClient: AuthClient;
    appConfig: AppConfig;
}

export const AclProviderContext = createContext<AclContextValue | null>(null);

export const AclProvider = ({ children }: PropsWithChildren) => {
    const { platoUser } = useAppState();

    const { data: appConfig } = api.platoUser.query.appConfig.getAppConfig.useQuery(undefined, {
        enabled: !!platoUser,
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchInterval: 1000 * 60, // 1 minute
    });

    const aclClient = useMemo(() => new AuthClient(appConfig), [appConfig]);

    const contextValue = useMemo(
        () =>
            appConfig
                ? {
                      authClient: aclClient,
                      appConfig,
                  }
                : undefined,
        [aclClient, appConfig],
    );

    if (!contextValue) {
        return <LoadingBlockFullPage />;
    }

    return <AclProviderContext.Provider value={contextValue}>{children}</AclProviderContext.Provider>;
};

/**
 * @deprecated Use useAppConfig instead for accessing app configuration
 */
export const useAcl = (): AuthClient => {
    const context = useContext(AclProviderContext);

    if (!context) {
        throw new Error('useAcl must be used within a AclProvider');
    }

    return context.authClient;
};

export const useAppConfig = (): AppConfig => {
    const context = useContext(AclProviderContext);

    if (!context) {
        throw new Error('useAppConfig must be used within a AclProvider');
    }

    return context.appConfig;
};
